import { useCallback, useEffect, useRef, useState } from 'react';
import { getAuthorizationErrors } from '../services/api';
import type {
  AuthorizationBudget,
  AuthorizationError,
} from '../services/api/types.gen';

export interface UseAuthorizationBudgetResult {
  /** Per-authorization used/allowed for the Tasks tab budget strip. */
  budget: AuthorizationBudget[];
  /**
   * Whether the episode's insurance requires an authorization on file at all.
   * Gates the create-dialog capacity block: false => never
   * restricted, and the strip renders nothing rather than a neutral empty
   * state.
   */
  requiresAuthorization: boolean;
  errors: AuthorizationError[];
  errorCount: number;
  /**
   * The request failed, so used/allowed is unknown rather than absent. Kept
   * apart from an empty budget so the strip can say "unavailable" instead of
   * reading as "this episode needs no authorization".
   */
  unavailable: boolean;
  /**
   * True only while the *first* fetch for an episode is in flight — "no
   * authorization" and "not fetched yet" are both empty, and only the second
   * should show a placeholder.
   */
  loading: boolean;
  /** Re-run the fetch; callers use this after a visit create/edit/delete. */
  refresh: () => Promise<void>;
}

/**
 * Owns the selected episode's authorization budget and errors.
 *
 * Extracted from `PatientDetail` so the two orchestration behaviors below can
 * be tested without standing up that whole page. Both exist to keep the Tasks
 * calendar's strips from misbehaving on a panel the scheduler is actively
 * using, and neither is visible in the values this returns:
 *
 * - **Request-id guard.** The id is bumped before any early return, and every
 *   write is gated on it. Switching episodes therefore supersedes an in-flight
 *   fetch for the previous one — without it, a slower response for episode A
 *   can land after the scheduler moved to B and show A's budget on B's
 *   calendar.
 * - **First-fetch-only placeholder.** `refresh` re-runs after every visit
 *   create/edit/delete. Swapping numbers that are still correct for grey
 *   placeholders reads as a flicker, so only the first fetch for an episode
 *   sets `loading`; later refreshes redraw in place.
 */
export const useAuthorizationBudget = (
  episodeId: string | null | undefined
): UseAuthorizationBudgetResult => {
  const [budget, setBudget] = useState<AuthorizationBudget[]>([]);
  const [requiresAuthorization, setRequiresAuthorization] = useState(false);
  const [errors, setErrors] = useState<AuthorizationError[]>([]);
  const [errorCount, setErrorCount] = useState(0);
  const [unavailable, setUnavailable] = useState(false);
  const [loading, setLoading] = useState(false);

  // Only the latest fetch may write state.
  const requestRef = useRef(0);
  // Whether this episode's outcome is already on screen.
  const onScreenRef = useRef(false);

  const reset = useCallback(() => {
    setErrorCount(0);
    setErrors([]);
    setBudget([]);
    setRequiresAuthorization(false);
    setUnavailable(false);
    onScreenRef.current = false;
  }, []);

  const refresh = useCallback(async () => {
    // Bumped before any early return, so an episode switch supersedes a fetch
    // already in flight for the previous one.
    const requestId = ++requestRef.current;
    if (!episodeId) {
      reset();
      setLoading(false);
      return;
    }
    if (!onScreenRef.current) {
      setLoading(true);
    }
    try {
      // throwOnError so HTTP failures reach the catch below; the client's
      // default (false) resolves an error result whose `data` is undefined,
      // which reads exactly like "authorization not required" and silently
      // hides the strip.
      const res = await getAuthorizationErrors({
        path: { episode_id: episodeId },
        throwOnError: true,
      });
      // A newer episode selection superseded this fetch; drop the response.
      if (requestId !== requestRef.current) return;
      const nextErrors = res.data?.errors ?? [];
      setErrors(nextErrors);
      setErrorCount(res.data?.count ?? nextErrors.length);
      setBudget(res.data?.budget ?? []);
      setRequiresAuthorization(res.data?.requires_authorization ?? false);
      setUnavailable(false);
    } catch (err) {
      if (requestId !== requestRef.current) return;
      console.error('Failed to load episode authorization errors', err);
      // The counts are unknown, not zero. Clear them but flag the budget as
      // unavailable so the strip says so instead of reading as "this episode
      // needs no authorization".
      setErrorCount(0);
      setErrors([]);
      setBudget([]);
      setRequiresAuthorization(false);
      setUnavailable(true);
    } finally {
      // A superseded fetch must not clear the placeholder the newer one is
      // still waiting behind.
      if (requestId === requestRef.current) {
        setLoading(false);
        // Settled either way — a budget, or the "unavailable" note. Both are
        // content, so the next refresh redraws rather than blanking.
        onScreenRef.current = true;
      }
    }
  }, [episodeId, reset]);

  useEffect(() => {
    // Clear the previous episode's values the moment the selection changes;
    // the fetch below repopulates them. Without this the old episode's budget
    // and errors stay on screen for the length of the request.
    reset();
  }, [episodeId, reset]);

  return {
    budget,
    requiresAuthorization,
    errors,
    errorCount,
    unavailable,
    loading,
    refresh,
  };
};

export default useAuthorizationBudget;
